import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import {
  adminMobileDrawerCloseButtonProps,
  adminMobileDrawerHeaderProps,
  adminMobileDrawerShellProps,
  adminMobileDrawerTitleProps,
} from "../../../constants/adminSidebarTheme";
import { useAdminTheme } from "../../../context/AdminThemeContext";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AiOutlineMenu } from "react-icons/ai";
import { RiCloseLine } from "react-icons/ri";
import {
  ASSISTANT_ALLOWED_PATHS,
  roleNameFromUser,
} from "../../../constants/assistantAccess";
import TeacherSidenavbar from "./teacherSidenavbar";
import SuperAdminSidenavbar from "./superAdminSidenavbar";
import LogoutMenu from "../Layout/logout-menu";
import AdminNotificationsBell from "../Notifications/AdminNotificationsBell";
import ThemeToggleButton from "../ThemeToggleButton";

/** Total height of fixed mobile bar including safe-area inset (sync with Layout padding). */
export const MOBILE_TOP_BAR_OFFSET = "calc(56px + env(safe-area-inset-top, 0px))";

export default function MobileSidebar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef();
  const userData = useSelector((state) => state.user.userData);
  const location = useLocation();

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  const roleName = roleNameFromUser(userData);
  const isTeacher = roleName === "Teacher";
  const isAssistant = roleName === "Assistant";
  const { themeMode } = useAdminTheme();
  const isDark = themeMode === "dark";

  return (
    <>
      <Flex
        display={{ base: "flex", md: "none" }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1300}
        minH={MOBILE_TOP_BAR_OFFSET}
        pt="env(safe-area-inset-top, 0px)"
        pb={2}
        px={3}
        align="center"
        justify="space-between"
        bg="var(--surface-raised)"
        borderBottom="1px solid"
        borderColor="var(--border-subtle)"
        backdropFilter="blur(8px)"
        boxShadow="0 2px 12px rgba(91, 13, 22, 0.06)"
      >
        <IconButton
          ref={btnRef}
          aria-label="Open navigation menu"
          icon={<AiOutlineMenu size={22} />}
          onClick={onOpen}
          size="md"
          borderRadius="full"
          bg={brandPalette.primary}
          color={brandPalette.cream}
          _hover={{ bg: brandPalette.secondary }}
          _active={{ bg: brandPalette.secondary }}
        />
        <Flex align="center" gap={2} flexShrink={0}>
          {roleName && !isAssistant ? (
            <AdminNotificationsBell
              iconColor={brandPalette.primary}
              hoverBg="rgba(91, 13, 22, 0.08)"
              variant="ghost"
              size="md"
            />
          ) : null}
          <ThemeToggleButton tone="onSurface" size="md" iconSize={18} />
          <LogoutMenu compact />
        </Flex>
      </Flex>

      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        finalFocusRef={btnRef}
        closeOnOverlayClick
        closeOnEsc
        size="xs"
      >
        <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <DrawerContent {...adminMobileDrawerShellProps(isDark)}>
          <DrawerHeader {...adminMobileDrawerHeaderProps(isDark)}>
            <Text {...adminMobileDrawerTitleProps(isDark)}>Navigation</Text>
            <IconButton
              aria-label="Close menu"
              icon={<RiCloseLine size={24} />}
              onClick={onClose}
              {...adminMobileDrawerCloseButtonProps(isDark)}
            />
          </DrawerHeader>

          <DrawerBody p={0} overflowY="auto" flex="1" maxH="calc(100vh - 60px)">
            {roleName ? (
              isTeacher || isAssistant ? (
                <TeacherSidenavbar
                  embedded
                  allowedPaths={isAssistant ? ASSISTANT_ALLOWED_PATHS : null}
                  hideNotifications={isAssistant}
                />
              ) : (
                <SuperAdminSidenavbar embedded />
              )
            ) : null}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
